import Icon from '../ui/Icon';
import Button from '../ui/Button';
import { CATEGORIES } from './feedbackMeta';
import { CategoryBadge, GuestBadge } from './FeedbackTable';
import StatusBadge from '../ui/StatusBadge';

function AttachmentLink({ attachment }) {
  const meta = [attachment.sizeLabel, attachment.isPdf ? 'PDF' : attachment.isImage ? 'Image' : null].filter(Boolean).join(' • ');
  const content = (
    <>
      {attachment.isImage && attachment.url ? (
        <img
          src={attachment.url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-14 h-14 rounded-lg object-cover border border-outline-variant/30 bg-surface-container shrink-0"
        />
      ) : (
        <span className="w-14 h-14 rounded-lg flex items-center justify-center bg-surface-container text-on-surface-variant shrink-0">
          <Icon name={attachment.isPdf ? 'picture_as_pdf' : 'draft'} size={26} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block font-data-mono text-xs font-semibold text-on-surface truncate">{attachment.fileName}</span>
        {meta && <span className="block text-[11px] text-on-surface-variant">{meta}</span>}
      </span>
    </>
  );
  if (!attachment.url) return <div className="flex items-center gap-3">{content}</div>;
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${attachment.fileName} in a new tab`}
      className="flex items-center gap-3 rounded-lg p-1 -m-1 hover:bg-surface-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
    >
      {content}
    </a>
  );
}

function MetaItem({ icon, label, children }) {
  return (
    <span className="inline-flex items-center gap-1" title={label}>
      <Icon name={icon} size={14} />
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  );
}

/** "Original submission" card with category select and Close / Reopen. */
export function OriginalSubmission({ ticket, savingCategory, savingStatus, onCategoryChange, onClose, onReopen }) {
  const closed = ticket.statusKey === 'closed';
  return (
    <section aria-labelledby="ticket-original" className="rounded-xl bg-surface-container-low p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <h3 id="ticket-original" className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
          Original submission
        </h3>
        <span className="font-data-mono text-[11px] text-on-surface-variant text-right">{ticket.createdExact}</span>
      </div>
      {ticket.customer.isGuest && (
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
          <span className="font-semibold text-on-surface">{ticket.customer.name}</span>
          <GuestBadge />
          {ticket.customer.email && <span className="font-data-mono text-on-surface-variant break-all">{ticket.customer.email}</span>}
        </p>
      )}
      <div>
        <p className="text-sm font-bold text-on-surface">{ticket.subject}</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-on-surface-variant whitespace-pre-wrap break-words">{ticket.message}</p>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-on-surface-variant font-data-mono">
        <MetaItem icon="confirmation_number" label="Ticket">
          #{ticket.ticketNo}
        </MetaItem>
        {ticket.source && (
          <MetaItem icon={ticket.source.icon} label="Source">
            {ticket.source.label}
          </MetaItem>
        )}
        {ticket.appVersion && (
          <MetaItem icon="info" label="App version">
            v{ticket.appVersion}
          </MetaItem>
        )}
        {ticket.deviceInfo && (
          <MetaItem icon="devices" label="Device">
            {ticket.deviceInfo}
          </MetaItem>
        )}
        {ticket.language && (
          <MetaItem icon="translate" label="Language">
            {ticket.language}
          </MetaItem>
        )}
        {ticket.customer.userId && (
          <MetaItem icon="person" label="Customer ID">
            {ticket.customer.userId}
          </MetaItem>
        )}
      </div>

      {ticket.attachments.length > 0 && (
        <ul className="flex flex-col gap-2 pt-3 border-t border-outline-variant/30" aria-label="Customer attachments">
          {ticket.attachments.map((a) => (
            <li key={a.id}>
              <AttachmentLink attachment={a} />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-outline-variant/30">
        <label htmlFor="ticket-category" className="sr-only">
          Category
        </label>
        <div className="relative">
          <select
            id="ticket-category"
            value={ticket.categoryKey}
            disabled={savingCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="h-8 appearance-none pl-2.5 pr-7 rounded-lg border border-outline-variant/50 bg-surface-container-lowest text-xs font-semibold text-on-surface outline-none focus:border-primary-container disabled:opacity-60"
          >
            {!CATEGORIES.some((c) => c.key === ticket.categoryKey) && <option value={ticket.categoryKey}>{ticket.category.label}</option>}
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <Icon name="expand_more" size={16} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-on-surface-variant" />
        </div>
        <CategoryBadge category={ticket.category} />
        <StatusBadge tone={ticket.status.tone}>{ticket.status.label}</StatusBadge>
        <span className="flex-1" />
        {closed ? (
          <Button variant="secondary" icon="lock_open" onClick={onReopen} disabled={savingStatus}>
            {savingStatus ? 'Reopening…' : 'Reopen'}
          </Button>
        ) : (
          <Button variant="secondary" icon="task_alt" onClick={onClose} disabled={savingStatus}>
            Close ticket
          </Button>
        )}
      </div>
    </section>
  );
}

/** Replies so far, oldest first. Failed ones can be sent again. */
export function Conversation({ replies, onSendAgain }) {
  if (!replies.length) return null;
  return (
    <section aria-labelledby="ticket-conversation" className="flex flex-col gap-2">
      <h3 id="ticket-conversation" className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
        Conversation
      </h3>
      <ol className="flex flex-col gap-2">
        {replies.map((r) => (
          <li
            key={r.id}
            className={`rounded-xl border p-3 flex flex-col gap-1.5 ${r.delivered ? 'border-outline-variant/30 bg-surface-container-lowest' : 'border-status-alert-border bg-status-alert-bg/40'}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold text-on-surface">{r.adminName}</span>
              <span className="flex items-center gap-2">
                <span className="font-data-mono text-[11px] text-on-surface-variant">{r.createdExact}</span>
                {r.delivered ? (
                  <StatusBadge tone="ok">Sent</StatusBadge>
                ) : (
                  <span title={r.errorCode ? `Error: ${r.errorCode}` : undefined}>
                    <StatusBadge tone="alert">Not delivered</StatusBadge>
                  </span>
                )}
              </span>
            </div>
            {r.subject && <p className="text-xs font-semibold text-on-surface">{r.subject}</p>}
            <p className="text-xs leading-relaxed text-on-surface-variant whitespace-pre-wrap break-words">{r.body}</p>
            {r.attachments.length > 0 && (
              <ul className="flex flex-wrap gap-x-3 gap-y-1" aria-label="Reply attachments">
                {r.attachments.map((a) => (
                  <li key={a.id}>
                    {a.url ? (
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-data-mono text-secondary hover:underline"
                      >
                        <Icon name={a.isPdf ? 'picture_as_pdf' : 'image'} size={14} />
                        {a.fileName}
                      </a>
                    ) : (
                      <span className="text-[11px] font-data-mono text-on-surface-variant">{a.fileName}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!r.delivered && (
              <div>
                <Button variant="secondary" size="sm" icon="forward_to_inbox" onClick={() => onSendAgain(r)}>
                  Send again
                </Button>
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
